import React from 'react';
import { CreditCard, Target, Briefcase, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import Disclaimer from '../ui/Disclaimer';
import DisclaimerContent from '../../utils/disclaimerContent';
import { formatCurrency, getLoanPurpose, getEmploymentStatus, getPropertyStatus } from '../../utils/formatting';

type ApplicationReviewFormProps = {
  formData: FormData;
  onContinue: () => void;
  onEditAllDetails: () => void;
};

type ReviewRow = {
  key: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
};

export default function ApplicationReviewForm({
  formData,
  onContinue,
  onEditAllDetails,
}: ApplicationReviewFormProps) {
  const { t } = useTranslation('wizard');
  const notSpecified = t('applicationReview.notSpecified');

  const rows: ReviewRow[] = [
    {
      key: 'loan-amount',
      icon: CreditCard,
      label: t('applicationReview.rowLabels.loanAmount'),
      value: formData.loanAmount ? formatCurrency(formData.loanAmount) : notSpecified,
    },
    {
      key: 'loan-purpose',
      icon: Target,
      label: t('applicationReview.rowLabels.loanPurpose'),
      value: formData.loanPurpose ? getLoanPurpose(formData.loanPurpose) : notSpecified,
    },
    {
      key: 'employment-status',
      icon: Briefcase,
      label: t('applicationReview.rowLabels.employmentStatus'),
      value: formData.employmentStatus ? getEmploymentStatus(formData.employmentStatus) : notSpecified,
    },
    {
      key: 'property-status',
      icon: Home,
      label: t('applicationReview.rowLabels.propertyStatus'),
      value: formData.propertyStatus ? getPropertyStatus(formData.propertyStatus) : notSpecified,
    },
  ];

  return (
    <div id="application-review-form" className="space-y-6 pb-24 sm:pb-0">
      <div className="text-center space-y-4">
        <div>
          <Title
            id="application-review-title"
            name="application-review-title"
            as="h1"
            className="mb-2"
          >
            {t('applicationReview.title')}
          </Title>
          <Description
            id="application-review-description"
            name="application-review-description"
            size="lg"
          >
            {t('applicationReview.description')}
          </Description>
        </div>
      </div>

      <div id="application-review-details" className="bg-gray-50 rounded-lg p-6 text-left">
        <h3 className="font-semibold text-gray-900 mb-4">{t('applicationReview.detailsHeading')}</h3>

        <div className="divide-y divide-gray-200">
          {rows.map(({ key, icon: Icon, label, value }) => (
            <div
              key={key}
              id={`application-review-row-${key}`}
              className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
            >
              <Icon className="w-5 h-5 text-[#212d52] shrink-0" />
              <div className="min-w-0">
                <div className="text-sm text-gray-600">{label}</div>
                <div className="font-medium text-gray-900 truncate">{value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {/*
          Continue is pinned to the viewport bottom on mobile (removed from
          normal flow), so it's ordered first here purely for the desktop
          (sm+) stacked layout, where it becomes static again.
        */}
        <div
          id="application-review-continue-bar"
          className="order-2 sm:order-1 fixed inset-x-0 bottom-0 z-40 bg-white p-4 border-t border-gray-200 shadow-[0_-4px_12px_-2px_rgba(0,0,0,0.08)] sm:static sm:z-auto sm:bg-transparent sm:p-0 sm:border-0 sm:shadow-none"
        >
          <div className="max-w-2xl mx-auto grid grid-cols-1 gap-3 sm:mx-0 sm:max-w-none">
            <Button
              id="application-review-continue"
              name="application-review-continue"
              fullWidth
              size="lg"
              onClick={onContinue}
            >
              {t('applicationReview.continue')}
            </Button>
          </div>
        </div>

        {/*
          Edit Details stays in normal flow, so on mobile it renders directly
          below the details card (Continue floats away to the fixed bar), and
          on desktop it's ordered after Continue in the stacked layout.
        */}
        <div className="order-1 sm:order-2">
          <Button
            id="application-review-edit-details"
            name="application-review-edit-details"
            variant="outline"
            fullWidth
            size="lg"
            onClick={onEditAllDetails}
          >
            {t('applicationReview.editDetails')}
          </Button>
        </div>
      </div>

      <Disclaimer
        id="application-review-disclaimer"
        name="application-review-disclaimer"
      >
        <DisclaimerContent />
      </Disclaimer>
    </div>
  );
}
